/**
 * Perioada paginii de rapoarte financiare — stare pură, partajată cu aplicația
 * nativă (`mobile/` o importă prin aliasul `@web/*`).
 *
 * Oglindește `backend/src/modules/financials/financials.reportPeriod.ts`: patru
 * tipuri de perioadă, cu aceleași chei (`2026-09`, `2026-Q3`, `2026`,
 * `2026-09-01..2026-10-15`), ca un link către raport să fie reproductibil.
 *
 * Datele se manipulează ca string-uri `YYYY-MM-DD` și cu `Date.UTC`, niciodată
 * prin `toISOString()` pe un `Date` local — pe ora României ar scădea o zi
 * (aceeași capcană documentată în `components/ui/DateField.tsx`).
 */
import type { Locale } from './i18n';

export type ReportPeriodKind = 'month' | 'quarter' | 'year' | 'custom';
export type ReportCompareMode = 'prev' | 'yoy';

export interface ReportPeriodState {
  kind: ReportPeriodKind;
  year: number;
  /** month kind */
  month: number;
  /** quarter kind */
  quarter: number;
  /** custom kind, `YYYY-MM-DD` */
  from: string;
  to: string;
  compare: ReportCompareMode;
}

const INTL_LOCALE: Record<Locale, string> = { ro: 'ro-RO', en: 'en-GB' };

export function shiftMonth(
  year: number,
  month: number,
  by: number,
): { year: number; month: number } {
  const idx = year * 12 + (month - 1) + by;
  return { year: Math.floor(idx / 12), month: (idx % 12) + 1 };
}

export function quarterOf(month: number): number {
  return Math.floor((month - 1) / 3) + 1;
}

/** `YYYY-MM-DD` pentru o zi calendaristică, fără drum prin fusul local. */
export function dayKey(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Luna precedentă: o perioadă se raportează după ce s-a încheiat. */
export function defaultReportPeriod(now = new Date()): ReportPeriodState {
  const { year, month } = shiftMonth(now.getFullYear(), now.getMonth() + 1, -1);
  return {
    kind: 'month',
    year,
    month,
    quarter: quarterOf(month),
    from: dayKey(year, month, 1),
    to: dayKey(year, month, lastDayOfMonth(year, month)),
    compare: 'prev',
  };
}

/** Cheia perioadei — aceeași pe care o întoarce backendul în `report.period`. */
export function periodKey(state: ReportPeriodState): string {
  switch (state.kind) {
    case 'month':
      return `${state.year}-${String(state.month).padStart(2, '0')}`;
    case 'quarter':
      return `${state.year}-Q${state.quarter}`;
    case 'year':
      return String(state.year);
    case 'custom':
      return `${state.from}..${state.to}`;
  }
}

/** Query string pentru `/api/financials/report*`. */
export function periodParams(state: ReportPeriodState): Record<string, string> {
  const base = { kind: state.kind, compare: state.compare };
  switch (state.kind) {
    case 'month':
      return { ...base, year: String(state.year), month: String(state.month) };
    case 'quarter':
      return { ...base, year: String(state.year), quarter: String(state.quarter) };
    case 'year':
      return { ...base, year: String(state.year) };
    case 'custom':
      return { ...base, from: state.from, to: state.to };
  }
}

const KINDS: ReportPeriodKind[] = ['month', 'quarter', 'year', 'custom'];
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Starea din URL (`?kind=&y=&m=&q=&from=&to=&cmp=`), cu fallback pe implicit. */
export function periodFromParams(params: URLSearchParams, now = new Date()): ReportPeriodState {
  const fallback = defaultReportPeriod(now);
  const kindParam = params.get('kind');
  const kind = KINDS.includes(kindParam as ReportPeriodKind)
    ? (kindParam as ReportPeriodKind)
    : fallback.kind;
  const year = intParam(params.get('y'), 2000, 2200) ?? fallback.year;
  const month = intParam(params.get('m'), 1, 12) ?? fallback.month;
  const quarter = intParam(params.get('q'), 1, 4) ?? quarterOf(month);
  const from = dayParam(params.get('from')) ?? fallback.from;
  const to = dayParam(params.get('to')) ?? fallback.to;
  const cmp = params.get('cmp');
  const compare: ReportCompareMode = cmp === 'yoy' ? 'yoy' : 'prev';
  // Un interval inversat în URL nu are sens: se întoarce la implicit.
  if (kind === 'custom' && to < from) {
    return { ...fallback, kind: 'custom', compare };
  }
  return { kind, year, month, quarter, from, to, compare };
}

/** Starea → parametri de URL, doar cei care au sens pentru tipul curent. */
export function periodToParams(state: ReportPeriodState): Record<string, string> {
  const out: Record<string, string> = { kind: state.kind, cmp: state.compare };
  if (state.kind === 'custom') {
    out.from = state.from;
    out.to = state.to;
    return out;
  }
  out.y = String(state.year);
  if (state.kind === 'month') out.m = String(state.month);
  if (state.kind === 'quarter') out.q = String(state.quarter);
  return out;
}

function intParam(raw: string | null, min: number, max: number): number | null {
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
}

function dayParam(raw: string | null): string | null {
  if (raw === null || !DAY_RE.test(raw)) return null;
  const [y, m, d] = raw.split('-').map(Number);
  const probe = new Date(Date.UTC(y, m - 1, d));
  const real =
    probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
  return real ? raw : null;
}

/** Pasul `‹ ›`: perioada precedentă / următoare, de aceeași lungime. */
export function shiftPeriod(state: ReportPeriodState, by: number): ReportPeriodState {
  switch (state.kind) {
    case 'month': {
      const m = shiftMonth(state.year, state.month, by);
      return { ...state, year: m.year, month: m.month, quarter: quarterOf(m.month) };
    }
    case 'quarter': {
      const idx = state.year * 4 + (state.quarter - 1) + by;
      const year = Math.floor(idx / 4);
      const quarter = (idx % 4) + 1;
      return { ...state, year, quarter, month: (quarter - 1) * 3 + 1 };
    }
    case 'year':
      return { ...state, year: state.year + by };
    case 'custom': {
      const span = dayDiff(state.from, state.to) + 1;
      return {
        ...state,
        from: addDays(state.from, by * span),
        to: addDays(state.to, by * span),
      };
    }
  }
}

/** Trecerea de la un tip de perioadă la altul păstrează punctul în timp. */
export function switchKind(state: ReportPeriodState, kind: ReportPeriodKind): ReportPeriodState {
  if (kind === state.kind) return state;
  const next: ReportPeriodState = { ...state, kind };
  if (kind === 'quarter') next.quarter = quarterOf(state.month);
  if (kind === 'custom') {
    if (state.kind === 'month') {
      next.from = dayKey(state.year, state.month, 1);
      next.to = dayKey(state.year, state.month, lastDayOfMonth(state.year, state.month));
    } else if (state.kind === 'quarter') {
      const first = (state.quarter - 1) * 3 + 1;
      next.from = dayKey(state.year, first, 1);
      next.to = dayKey(state.year, first + 2, lastDayOfMonth(state.year, first + 2));
    } else {
      next.from = dayKey(state.year, 1, 1);
      next.to = dayKey(state.year, 12, 31);
    }
  }
  return next;
}

function toUtc(day: string): number {
  const [y, m, d] = day.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

export function dayDiff(from: string, to: string): number {
  return Math.round((toUtc(to) - toUtc(from)) / 86_400_000);
}

export function addDays(day: string, by: number): string {
  const d = new Date(toUtc(day) + by * 86_400_000);
  return dayKey(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

/** `Martie 2026` / `T3 2026` / `2026` / `10 sept. – 5 oct. 2026`. */
export function periodTitle(state: ReportPeriodState, locale: Locale = 'ro'): string {
  const intl = INTL_LOCALE[locale];
  switch (state.kind) {
    case 'month': {
      const label = new Intl.DateTimeFormat(intl, {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(Date.UTC(state.year, state.month - 1, 1)));
      return label.charAt(0).toUpperCase() + label.slice(1);
    }
    case 'quarter':
      return `${locale === 'ro' ? 'T' : 'Q'}${state.quarter} ${state.year}`;
    case 'year':
      return String(state.year);
    case 'custom': {
      const fmt = new Intl.DateTimeFormat(intl, { dateStyle: 'medium', timeZone: 'UTC' });
      return `${fmt.format(new Date(toUtc(state.from)))} – ${fmt.format(new Date(toUtc(state.to)))}`;
    }
  }
}

/** Eticheta scurtă a unui bucket de grafic, după granularitatea lui. */
export function bucketLabel(period: string, kind: ReportPeriodKind, locale: Locale = 'ro'): string {
  if (kind === 'year') return period;
  if (kind === 'quarter') {
    const [y, q] = period.split('-Q');
    return `${locale === 'ro' ? 'T' : 'Q'}${q} ${y.slice(2)}`;
  }
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, 1)));
}

/** Numele trimestrelor pentru selector. */
export function quarterNames(locale: Locale = 'ro'): string[] {
  const prefix = locale === 'ro' ? 'Trimestrul' : 'Quarter';
  return [1, 2, 3, 4].map((q) => `${prefix} ${q}`);
}
